import { DEFAULT_PER_PAGE, type SearchParams, type FilterState } from '@/types/cars';
import { sortMapping } from '@/utils/SortingUtil';

/**
 * Parse URL search parameters into FilterState
 */
export function parseSearchParams(params: SearchParams): FilterState {
  const filters: FilterState = {};

  const collection = params['collection'];
  if (collection) {
    const collectionValue = Array.isArray(collection) ? collection[0] : collection;
    if (collectionValue) {
      filters.collection = collectionValue.toLowerCase();
    }
  }
  
  // Parse array parameters
  const arrayParams = ['make', 'model', 'bodystyle', 'doors', 'cylinder', 'fuel', 
                      'engine', 'train', 'transmission', 'incolor', 'excolor'];
  
  arrayParams.forEach(param => {
    const value = params[param];
    if (value) {
      (filters as any)[param] = Array.isArray(value) ? value : [value];
    }
  });
  
  // Parse range parameters
  if (params['year']) {
    const yearValues = Array.isArray(params['year']) ? params['year'] : [params['year']];
    if (yearValues.length === 2 && yearValues[0] && yearValues[1]) {
      filters.year = [parseInt(yearValues[0]), parseInt(yearValues[1])];
    }
  }
  
  if (params['price']) {
    const priceValues = Array.isArray(params['price']) ? params['price'] : [params['price']];
    if (priceValues.length === 2 && priceValues[0] && priceValues[1]) {
      filters.price = [parseInt(priceValues[0]), parseInt(priceValues[1])];
    }
  }
  
  if (params['mileage']) {
    const mileageValues = Array.isArray(params['mileage']) ? params['mileage'] : [params['mileage']];
    if (mileageValues.length === 2 && mileageValues[0] && mileageValues[1]) {
      filters.mileage = [parseInt(mileageValues[0]), parseInt(mileageValues[1])];
    }
  }
  
  // Parse single value parameters with conditional spreading
  const q = params['q'];
  if (q) {
    const queryValue = Array.isArray(q) ? q[0] : q;
    if (queryValue) {
      filters.q = queryValue;
    }
  }
  
  const sort = params['sort'];
  if (sort) {
    const sortValue = Array.isArray(sort) ? sort[0] : sort;
    if (sortValue) {
      filters.sort = sortValue as any;
    }
  }
  
  const page = params['page'];
  if (page) {
    const pageValue = Array.isArray(page) ? page[0] : page;
    if (pageValue) {
      const parsed = /^\d+$/.test(pageValue) ? Number(pageValue) : NaN;
      if (Number.isSafeInteger(parsed) && parsed > 0) filters.page = parsed;
    }
  }
  
  const limit = params['limit'] ?? params['perPage'];
  if (limit) {
    const limitValue = Array.isArray(limit) ? limit[0] : limit;
    if (limitValue) {
      const parsed = /^\d+$/.test(limitValue) ? Number(limitValue) : NaN;
      if (Number.isSafeInteger(parsed) && parsed > 0 && parsed <= 250) filters.limit = parsed;
    }
  }
  
  // Parse boolean parameters
  filters.shopOnline = params['shopOnline'] === 'true';
  
  return filters;
}

/**
 * Build filter query string from FilterState
 */
export function buildFilterQuery(filters: FilterState): string {
  const params = new URLSearchParams();

  if (filters.collection) {
    params.set('collection', filters.collection);
  }
  if (filters.q?.trim()) params.set('q', filters.q.trim());
  if (filters.shopOnline) params.set('shopOnline', 'true');
  
  // Add array filters
  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      if (key === 'year' || key === 'price' || key === 'mileage') {
        // Range parameters
        value.forEach(v => params.append(key, v.toString()));
      } else {
        // Multi-select parameters
        value.forEach(v => params.append(key, v));
      }
    }
  });
  
  // Add sort parameters
  if (filters.sort) {
    params.append('sort', filters.sort);
    // Also add orderBy and orderAsc for FilterListing compatibility
    const sortConfig = sortMapping[filters.sort];
    if (sortConfig) {
      params.append('orderBy', sortConfig.orderBy);
      params.append('orderAsc', sortConfig.orderAsc.toString());
    }
  }
  
  // Add pagination
  if (filters.page && filters.page !== 1) {
    params.append('page', filters.page.toString());
  }
  if (filters.limit && filters.limit !== DEFAULT_PER_PAGE) {
    params.append('perPage', filters.limit.toString());
  }
  
  return params.toString();
}

/**
 * Parse a single parameter value
 */
export function parseParam<T>(value: string | null, defaultValue: T): T {
  if (value === null) return defaultValue;
  
  // Handle boolean values
  if (typeof defaultValue === 'boolean') {
    return (value === 'true') as unknown as T;
  }
  
  // Handle number values
  if (typeof defaultValue === 'number') {
    const parsed = parseInt(value);
    return (isNaN(parsed) ? defaultValue : parsed) as unknown as T;
  }
  
  // Handle array values
  if (Array.isArray(defaultValue)) {
    return value.split(',') as unknown as T;
  }
  
  return value as unknown as T;
}

/**
 * Stringify a parameter value for URL
 */
export function stringifyParam<T>(value: T): string {
  if (Array.isArray(value)) {
    return value.join(',');
  }
  
  return String(value);
}
